import React, { useRef, useState, useEffect } from "react";
import { Volume2, VolumeX } from "lucide-react";

// Procedural atmospheric ambient pad (Web Audio API) — no external file, loops forever.
// Starts only on user click (browser autoplay policy) and can be toggled off.
const makeImpulse = (ctx, seconds, decay) => {
  const rate = ctx.sampleRate;
  const len = rate * seconds;
  const buf = ctx.createBuffer(2, len, rate);
  for (let ch = 0; ch < 2; ch++) {
    const data = buf.getChannelData(ch);
    for (let i = 0; i < len; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
    }
  }
  return buf;
};

const AmbientAudio = () => {
  const [on, setOn] = useState(false);
  const ref = useRef({ ctx: null, master: null });

  const start = () => {
    if (ref.current.ctx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    const ctx = new AC();

    const master = ctx.createGain();
    master.gain.value = 0;

    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 850;
    lp.Q.value = 0.7;

    const conv = ctx.createConvolver();
    conv.buffer = makeImpulse(ctx, 6, 2.1); // long, sacred reverb tail
    const wet = ctx.createGain();
    wet.gain.value = 0.9;
    const dry = ctx.createGain();
    dry.gain.value = 0.55;

    lp.connect(dry);
    dry.connect(master);
    lp.connect(conv);
    conv.connect(wet);
    wet.connect(master);
    master.connect(ctx.destination);

    // sacred open-fifth drone (organ / church-like)
    const freqs = [73.42, 110.0, 146.83, 220.0, 293.66]; // D2 A2 D3 A3 D4
    freqs.forEach((f, i) => {
      const o = ctx.createOscillator();
      o.type = i % 2 ? "sine" : "triangle";
      o.frequency.value = f;
      o.detune.value = (Math.random() - 0.5) * 6;
      const g = ctx.createGain();
      g.gain.value = (0.3 / freqs.length) * (i < 2 ? 1.4 : 0.8);
      o.connect(g);
      g.connect(lp);
      o.start();
    });

    // slow "breathing" amplitude
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.05;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0.03;
    lfo.connect(lfoGain);
    lfoGain.connect(master.gain);
    lfo.start();

    // slow filter sweep for movement
    const flfo = ctx.createOscillator();
    flfo.frequency.value = 0.02;
    const flfoGain = ctx.createGain();
    flfoGain.gain.value = 280;
    flfo.connect(flfoGain);
    flfoGain.connect(lp.frequency);
    flfo.start();

    // temple / Tao bell — slow meditative strikes with long shimmering decay
    const bellBus = ctx.createGain();
    bellBus.gain.value = 0.75;
    bellBus.connect(master);
    bellBus.connect(conv); // feed reverb for shimmer
    const partials = [1, 2.0, 2.76, 5.4, 8.9]; // inharmonic bell ratios
    const pGains = [1, 0.55, 0.45, 0.22, 0.12];
    const strikeBell = (time, fundamental) => {
      partials.forEach((ratio, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.value = fundamental * ratio;
        const dur = 4.2 - i * 0.35;
        g.gain.setValueAtTime(0.0001, time);
        g.gain.exponentialRampToValueAtTime(pGains[i], time + 0.004);
        g.gain.exponentialRampToValueAtTime(0.0001, time + dur);
        o.connect(g);
        g.connect(bellBus);
        o.start(time);
        o.stop(time + dur + 0.1);
      });
    };
    const notes = [196.0, 261.63, 220.0, 174.61]; // G3 C4 A3 F3 — calm
    let bi = 0;
    const ringBell = () => {
      strikeBell(ctx.currentTime + 0.05, notes[bi % notes.length]);
      bi++;
    };
    ringBell();
    const hb = setInterval(ringBell, 7000); // a gentle strike every 7s

    // dreamy monk choir — vowel-formant "aah" voices through the cathedral reverb
    const choirBus = ctx.createGain();
    choirBus.gain.value = 0.9;
    choirBus.connect(conv); // mostly reverberant
    const choirDry = ctx.createGain();
    choirDry.gain.value = 0.22;
    choirBus.connect(choirDry);
    choirDry.connect(master);
    const formants = [
      { f: 700, q: 7, g: 1.0 },
      { f: 1220, q: 9, g: 0.55 },
      { f: 2600, q: 11, g: 0.28 },
    ];
    const makeVoice = (freq) => {
      const vGain = ctx.createGain();
      vGain.gain.value = 0;
      [-6, 7].forEach((det) => {
        const o = ctx.createOscillator();
        o.type = "sawtooth";
        o.frequency.value = freq;
        o.detune.value = det;
        const vib = ctx.createOscillator();
        vib.frequency.value = 4.6 + Math.random() * 0.7;
        const vibG = ctx.createGain();
        vibG.gain.value = 3.5;
        vib.connect(vibG);
        vibG.connect(o.detune);
        vib.start();
        o.connect(vGain);
        o.start();
      });
      const sum = ctx.createGain();
      sum.gain.value = 0.5;
      formants.forEach(({ f, q, g }) => {
        const bp = ctx.createBiquadFilter();
        bp.type = "bandpass";
        bp.frequency.value = f;
        bp.Q.value = q;
        const fg = ctx.createGain();
        fg.gain.value = g;
        vGain.connect(bp);
        bp.connect(fg);
        fg.connect(sum);
      });
      sum.connect(choirBus);
      // slow breathing swell drives the voice level
      const swell = ctx.createOscillator();
      swell.frequency.value = 0.045 + Math.random() * 0.04;
      const swellG = ctx.createGain();
      swellG.gain.value = 0.22;
      const base = ctx.createConstantSource();
      base.offset.value = 0.24;
      swell.connect(swellG);
      swellG.connect(vGain.gain);
      base.connect(vGain.gain);
      swell.start();
      base.start();
    };
    [146.83, 220.0, 293.66, 349.23].forEach(makeVoice); // D3 A3 D4 F4

    master.gain.setTargetAtTime(0.22, ctx.currentTime, 2.0);
    ref.current = { ctx, master, hb };
  };

  const stop = () => {
    const { ctx, master, hb } = ref.current;
    if (!ctx) return;
    if (hb) clearInterval(hb);
    try {
      master.gain.setTargetAtTime(0, ctx.currentTime, 0.5);
    } catch (e) {}
    setTimeout(() => {
      ctx.close().catch(() => {});
      ref.current = { ctx: null, master: null };
    }, 900);
  };

  const toggle = () => {
    if (on) {
      stop();
      setOn(false);
    } else {
      start();
      setOn(true);
    }
  };

  // auto-start the ambient music on the first user interaction anywhere
  useEffect(() => {
    const onFirst = () => {
      if (!ref.current.ctx) {
        start();
        setOn(true);
      }
    };
    window.addEventListener("pointerdown", onFirst, { once: true });
    return () => window.removeEventListener("pointerdown", onFirst);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <button
      onClick={toggle}
      data-testid="ambient-audio-toggle"
      aria-label={on ? "Stlmiť hudbu" : "Zapnúť atmosférickú hudbu"}
      title={on ? "Stlmiť hudbu" : "Atmosférická hudba"}
      className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full flex items-center justify-center border border-white/15 bg-[#0a0a0a]/70 backdrop-blur-md text-[#d97925] hover:text-[#f5f2eb] hover:border-[#d97925] transition-colors"
    >
      {on && (
        <span className="absolute inset-0 rounded-full border border-[#d97925]/40 animate-ping" />
      )}
      {on ? <Volume2 size={18} /> : <VolumeX size={18} />}
    </button>
  );
};

export default AmbientAudio;
